import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AppSettings, CatalogStats, GroupDetail, LocalEpisode, SearchResult, SourceConfig } from '../types'

export type WorkerProgress = { sourceId?: string; message: string; current?: number; total?: number; done?: boolean; error?: boolean }
export type MetadataAvailabilityInput = { key: string; kind: 'movie' | 'series'; titles: string[]; year?: number }
export type MetadataAvailabilityMap = Record<string, { servers: string[]; count: number }>

type WorkerResponse =
  | { type: 'ready'; stats: CatalogStats }
  | { type: 'stats'; stats: CatalogStats }
  | { type: 'progress'; progress: WorkerProgress }
  | { type: 'search-results'; requestId: string; results: SearchResult[]; rawMatches: number }
  | { type: 'group-detail'; requestId: string; detail: GroupDetail }
  | { type: 'season-availability'; requestId: string; episodes: LocalEpisode[] }
  | { type: 'metadata-availability'; requestId: string; matches: MetadataAvailabilityMap }
  | { type: 'error'; message: string }

export function useCatalogWorker(settings: AppSettings) {
  const worker = useMemo(() => new Worker(new URL('../workers/catalog.worker.ts', import.meta.url), { type: 'module' }), [])
  const resolvers = useRef(new Map<string, (value: unknown) => void>())
  const progressTimer = useRef<number | null>(null)
  const [stats, setStats] = useState<CatalogStats>({ total: 0, movie: 0, series: 0, channel: 0, adult: 0, sources: [] })
  const [progress, setProgress] = useState<WorkerProgress | null>(null)
  const [ready, setReady] = useState(false)
  const [revision, setRevision] = useState(0)

  useEffect(() => {
    const onMessage = (event: MessageEvent<WorkerResponse>) => {
      const data = event.data
      if (data.type === 'ready') { setReady(true); setStats(data.stats); setRevision(value => value + 1) }
      if (data.type === 'stats') { setStats(data.stats); setRevision(value => value + 1) }
      if (data.type === 'progress') {
        if (progressTimer.current) window.clearTimeout(progressTimer.current)
        setProgress(data.progress)
        if (data.progress.done) progressTimer.current = window.setTimeout(() => setProgress(null), 3500)
      }
      if (data.type === 'search-results' || data.type === 'group-detail' || data.type === 'season-availability' || data.type === 'metadata-availability') {
        const resolver = resolvers.current.get(data.requestId)
        if (resolver) {
          resolver(
            data.type === 'search-results'
              ? { results: data.results, rawMatches: data.rawMatches }
              : data.type === 'group-detail'
                ? data.detail
                : data.type === 'season-availability'
                  ? data.episodes
                  : data.matches,
          )
          resolvers.current.delete(data.requestId)
        }
      }
      if (data.type === 'error') {
        if (progressTimer.current) window.clearTimeout(progressTimer.current)
        setProgress({ message: data.message, error: true })
        progressTimer.current = window.setTimeout(() => setProgress(null), 8000)
      }
    }
    worker.addEventListener('message', onMessage)
    worker.postMessage({ type: 'init', settings })
    return () => {
      if (progressTimer.current) window.clearTimeout(progressTimer.current)
      worker.removeEventListener('message', onMessage)
      worker.terminate()
    }
  }, [worker])

  useEffect(() => {
    worker.postMessage({ type: 'settings', settings })
  }, [worker, settings])

  const request = useCallback(<T,>(type: string, payload: unknown): Promise<T> => {
    const requestId = crypto.randomUUID()
    return new Promise<T>((resolve) => {
      resolvers.current.set(requestId, resolve as (value: unknown) => void)
      worker.postMessage({ type, requestId, payload })
    })
  }, [worker])
  const matchMetadataAvailability = useCallback(
    (items: MetadataAvailabilityInput[]) =>
      request<MetadataAvailabilityMap>('metadata-availability', { items }),
    [request],
  )

  return {
    ready, stats, progress, revision,
    importFile: (source: SourceConfig, file: File) => worker.postMessage({ type: 'import-file', source, file }),
    importM3uUrl: (source: SourceConfig) => worker.postMessage({ type: 'import-m3u-url', source }),
    importXtream: (source: SourceConfig, password: string, include: string[]) => worker.postMessage({ type: 'import-xtream', source, password, include }),
    search: (query: string, kinds: string[]) => request<{ results: SearchResult[]; rawMatches: number }>('search', { query, kinds }),
    getGroupDetail: (groupKey: string) => request<GroupDetail>('group-detail', { groupKey }),
    getSeasonAvailability: (groupKey: string, season: number, credentials: Array<{ source: SourceConfig; password: string }>) =>
      request<LocalEpisode[]>('season-availability', { groupKey, season, credentials }),
    matchMetadataAvailability,
    removeSource: (sourceId: string) => worker.postMessage({ type: 'remove-source', sourceId }),
    clearCatalog: () => worker.postMessage({ type: 'clear-catalog' }),
  }
}
